'use client';

import { Button } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { LottolandSecondarySmileLogo } from '../../../../../LottolandSecondarySmileLogo/LottolandSecondarySmileLogo';
import { DEPOSIT_PAYMENT_METHODS } from '../../../../../PaymentMethodSelector/PaymentMethods';
import type { RedirectionStepProps } from '../../types';

function RedirectionStep({ namespace, depositFlowInstance, ...props }: RedirectionStepProps) {
  const t = useTranslations(`${namespace}.redirection_step`);
  const { watch } = useFormContext();
  const paymentMethod = watch('amountStep.paymentMethod');
  const amount = watch('amountStep.amount');
  const [provider, setProvider] = useState<string | null>(null);
  const [method, setMethod] = useState<string | null>(null);

  useEffect(() => {
    const foundMethod = Object.values(DEPOSIT_PAYMENT_METHODS).find(paymentMethodObj => paymentMethodObj.name === paymentMethod);
    setProvider(foundMethod?.['data-track-provider'] ?? null);
    setMethod(foundMethod?.['data-track-method'] ?? null);
  }, [paymentMethod]);

  const { transactionRedirectionCheckData } = depositFlowInstance;

  const handleRedirect = () => {
    const newWindow = window.open(transactionRedirectionCheckData?.payload?.redirection?.url, '_blank');
    // eslint-disable-next-line react-hooks/immutability
    depositFlowInstance.newWindowRef.current = newWindow;
  };

  const getTranslationKey = useCallback(
    (baseKey: string) => {
      // Check if it's a known payment method
      const knownPaymentMethodNames = Object.values(DEPOSIT_PAYMENT_METHODS).map(method => method.name);
      const isKnownPaymentMethod = knownPaymentMethodNames.includes(paymentMethod);

      // If paymentMethod matches "Debit card" or it's not a known payment method (meaning it's a payment instrument/saved card)
      if (paymentMethod === DEPOSIT_PAYMENT_METHODS.CARD.name || !isKnownPaymentMethod) {
        return `payment_methods.debit_card.${baseKey}`;
      }
      if (paymentMethod === DEPOSIT_PAYMENT_METHODS.PAYBYBANK.name) {
        return `payment_methods.pay_by_bank.${baseKey}`;
      }
      return `payment_methods.default.${baseKey}`;
    },
    [paymentMethod]
  );
  // Extract tracking attributes from props for the button
  const trackType = props['data-track-type'];
  const trackCount = props['data-track-count'];
  const trackAmount = props['data-track-amount'];
  const trackOffer = props['data-track-offer'];

  return (
    <div className="w-full flex flex-col flex-1 gap-8 px-4" {...props}>
      <div className="flex flex-col gap-5">
        <LottolandSecondarySmileLogo className="rounded-sm" />
        <div className="flex flex-col gap-2">
          <h4>{t(getTranslationKey('title'))}</h4>
          <p className="text-foreground-subtle whitespace-pre-line">
            {t.rich(getTranslationKey('description'), {
              bold: chunks => <strong>{chunks}</strong>,
              amount,
            })}
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 mt-auto md:mt-0" data-track-container="Actions">
        <Button
          type="button"
          variant="secondary"
          className="w-full h-10"
          onClick={handleRedirect}
          data-track-name="Manual Handover"
          {...(method && { 'data-track-method': method })}
          {...(provider && { 'data-track-provider': provider })}
          {...(trackType && { 'data-track-type': trackType })}
          {...(trackCount && { 'data-track-count': trackCount })}
          {...(trackAmount && { 'data-track-amount': trackAmount })}
          {...(trackOffer && { 'data-track-offer': trackOffer })}
        >
          {t(getTranslationKey('button_link'))}
        </Button>
      </div>
    </div>
  );
}

export { RedirectionStep };
